import {
  type CollectionResourceLinkage,
  type NullResourceLinkage,
  ResourceLinkage,
  type SingleResourceLinkage,
} from '../ResourceLinkage'
import { type PersistedResourceIdentifier } from '../types'

export type PartialResourceObject<TType extends string> = Pick<
  PersistedResourceIdentifier<TType>,
  'id' | 'type'
>

/**
 * Accepts a JSON:API Resource Object (or array of Resource Objects), or any object with `id` and `type` properties, and
 * returns a Resource Linkage for use in `relationships`. An empty relationship can be represented by providing `null`
 * for a to-one relationship or an empty array (`[]`) for a to-many relationship.
 *
 * @param resources a single Resource Object or array of Resource Objects
 *
 * @note This accepts an arbitrary `TType`, and doesn’t validate that it’s part of our API.
 * @see https://jsonapi.org/format/#document-resource-object-linkage
 *
 * @example
 * linkageFromResource(null)
 * const block = factories.v3.resourceObjects.block.build()
 * linkageFromResource(block)
 * linkageFromResource({ id: block.id, type: block.type })
 *
 * @example
 * linkageFromResource([])
 * const users = factories.v3.resourceObjects.user.buildList()
 * linkageFromResource(users)
 * const userIds = users.map(({ id }) => id)
 * linkageFromResource(userIds.map((id) => { id, type: 'user' }))
 */
export function linkageFromResource(resources: null): NullResourceLinkage
export function linkageFromResource<TType extends string>(
  resources: PartialResourceObject<TType>
): SingleResourceLinkage<TType>
export function linkageFromResource<TType extends string>(
  resources: Array<PartialResourceObject<TType>>
): CollectionResourceLinkage<TType>

export function linkageFromResource<TType extends string>(
  resources:
    | PartialResourceObject<TType>
    | Array<PartialResourceObject<TType>>
    | null
) {
  if (Array.isArray(resources)) {
    return {
      data: resources.map(
        (resource) => new ResourceLinkage(resource).identifier
      ),
    }
  }

  return {
    data: resources ? new ResourceLinkage(resources).identifier : null,
  }
}
